import { KeyboardEventHandler, useCallback, useEffect, useState } from 'react';

import styled from '@emotion/styled';
import { PressableBox } from '@opendoor/bricks-next';
import { Box, List, ListItem } from '@opendoor/bricks/core';
import { novo } from '@opendoor/bricks/theme';
import Field from '@opendoor/cinderblocks/growth/novo/inputs/Field';
import { InputContainerElement } from '@opendoor/cinderblocks/growth/novo/inputs/Input';

export interface AutocompleteItem {
  /** Unique identifier for the item, used as React key for list rendering */
  id: string;
  /** Aria label for the item for accessibility */
  ariaLabel: string;
}

export interface AutocompleteInputProps<T extends AutocompleteItem> {
  /** Unique identifier for the input element */
  id: string;
  /** Label text displayed above the input */
  label?: string;
  /** Whether to visually hide the label while keeping it accessible */
  hideLabel?: boolean;
  /** Placeholder text shown when the input is empty */
  placeholder?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Array of items to display in the dropdown suggestions */
  items: T[];
  /** Current value of the search input */
  searchTerm: string;
  /** Callback fired when the search input value changes */
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Callback fired when a user selects an item from the dropdown */
  onItemSelect: (item: T) => void;
  /** Function to render each item in the dropdown list */
  renderItem: (item: T, isSelected: boolean) => React.ReactNode;
  /** Optional element to display at the end of the input (e.g., icon, button) */
  suffix?: React.ReactNode;
  /** Custom element to show within the suggestion list (e.g., "Add new" action) */
  customAction?: React.ReactNode;
  /** ID for the listbox element for accessibility */
  listBoxId?: string;
  /** Aria label for the listbox containing suggestions */
  listBoxAriaLabel: string;
  /** Optional callback fired when the Escape key is pressed */
  onEscape?: () => void;
  /** Optional callback fired when the input receives focus */
  onFocus?: () => void;
  /** Optional callback fired when the input loses focus */
  onBlur?: () => void;
}

// Override the field input container styles
const CustomFieldWrapper = styled.div<{ noValue: boolean }>`
  ${InputContainerElement} {
    outline: none;
    border: 1.5px solid ${novo.colors.warmgrey300};
    &:hover {
      outline: none;
    }
    &:focus-within {
      outline: none;
      border: 1.5px solid ${novo.colors.brandBlue600};
      border-top-left-radius: ${novo.radii.roundedSmooth};
      border-top-right-radius: ${novo.radii.roundedSmooth};
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border-bottom: none;
      ${(props) =>
        props.noValue &&
        `
        border-bottom-left-radius: ${novo.radii.roundedSmooth};
        border-bottom-right-radius: ${novo.radii.roundedSmooth};
        border-bottom: 1.5px solid ${novo.colors.brandBlue600};
      `}
    }
  }
`;

const ListBox = ({
  children,
  listBoxId,
  listBoxAriaLabel,
}: {
  children: React.ReactNode;
  listBoxId: string;
  listBoxAriaLabel: string;
}) => {
  return (
    <Box
      borderBottomRadius="roundedSmooth"
      maxHeight="450px"
      pt={2}
      position="absolute"
      top={['-11px', null, '-19px', '-27px']}
      w="100%"
      border={'1.5px solid #0040E6'}
      backgroundColor="white0"
      overflow="hidden"
    >
      <List variant="text" role="listbox" aria-label={listBoxAriaLabel} id={listBoxId}>
        {children}
      </List>
    </Box>
  );
};

const AutocompleteInput = <T extends AutocompleteItem>({
  id,
  label,
  hideLabel,
  placeholder,
  disabled,
  items,
  searchTerm,
  onSearchChange,
  onItemSelect,
  renderItem,
  suffix,
  customAction,
  listBoxId = 'autocomplete-listbox',
  listBoxAriaLabel,
  onEscape,
  onFocus,
  onBlur,
}: AutocompleteInputProps<T>) => {
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(undefined);
  const [isFocused, setIsFocused] = useState(false);

  // Reset selection when items change to prevent stale index
  useEffect(() => {
    if (selectedIndex !== undefined && selectedIndex >= items.length) {
      setSelectedIndex(undefined);
    }
  }, [items, selectedIndex]);

  const handleItemSelect = useCallback(
    (item: T) => () => {
      onItemSelect(item);
    },
    [onItemSelect],
  );

  const onKeyDown: KeyboardEventHandler<HTMLInputElement | HTMLButtonElement> = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (items.length > 0) {
        const newSelectedIndex =
          selectedIndex === undefined || selectedIndex === items.length - 1 ? 0 : selectedIndex + 1;
        setSelectedIndex(newSelectedIndex);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (items.length > 0) {
        const newSelectedIndex =
          selectedIndex === undefined || selectedIndex === 0 ? items.length - 1 : selectedIndex - 1;
        setSelectedIndex(newSelectedIndex);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex !== undefined && items[selectedIndex]) {
        onItemSelect(items[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      setSelectedIndex(undefined);
      onEscape?.();
    } else {
      setSelectedIndex(undefined);
    }
  };

  const handleContainerBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        setIsFocused(false);
        onBlur?.();
      }
    },
    [onBlur],
  );

  const handleContainerFocus = useCallback(() => {
    setIsFocused(true);
    onFocus?.();
  }, [onFocus]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  const showDropdown = searchTerm !== '' && isFocused;

  return (
    <Box
      position="relative"
      zIndex={showDropdown ? 10 : 'auto'}
      onFocus={handleContainerFocus}
      onBlur={handleContainerBlur}
    >
      <Box position={'relative'} zIndex={2}>
        <CustomFieldWrapper noValue={searchTerm === ''}>
          <Field
            label={hideLabel ? '' : label}
            input={{
              autoComplete: 'off',
              placeholder,
              disabled,
              id: id,
              onChange: onSearchChange,
              onKeyDown: onKeyDown,
              value: searchTerm ?? '',
              type: 'text',
              role: 'combobox',
              'aria-autocomplete': 'list',
              'aria-expanded': showDropdown ? 'true' : 'false',
              'aria-controls': listBoxId,
              suffix: suffix,
            }}
          />
        </CustomFieldWrapper>
      </Box>
      <Box position="relative">
        {showDropdown && (
          <ListBox listBoxId={listBoxId} listBoxAriaLabel={listBoxAriaLabel}>
            {items.map((item, index) => {
              const isSelected = selectedIndex === index;

              return (
                <ListItem
                  id="autocomplete-options"
                  key={item.id}
                  role="option"
                  aria-selected={isSelected || undefined}
                  overflow={'hidden'}
                >
                  <PressableBox
                    zIndex={2}
                    justifyContent="flex-start"
                    onMouseDown={handleMouseDown}
                    analyticsName=""
                    aria-label={item.ariaLabel}
                    animation="accelerateDecelerateFastest"
                    height="100%"
                    width="100%"
                    backgroundColor={
                      isSelected ? '$backgroundStateHoverPrimary' : '$backgroundPrimary'
                    }
                    hoverStyle={{
                      backgroundColor: '$backgroundStateHoverPrimary',
                    }}
                    onPress={handleItemSelect(item)}
                    pressStyle={{
                      scale: 0.98,
                    }}
                    padding={10}
                  >
                    {renderItem(item, isSelected)}
                  </PressableBox>
                </ListItem>
              );
            })}
            {customAction}
          </ListBox>
        )}
      </Box>
    </Box>
  );
};

export default AutocompleteInput;
